import React from 'react';
import ChartEmbed from './ChartEmbed';
import CardEmbed from './CardEmbed';
import StatCard from './StatCard';
import ActionButton from './ActionButton';
import DataTable from './DataTable';
import InteractiveEmbed from './InteractiveEmbed';
import './embeds.css';

const COMPONENT_MAP = {
  chart: ChartEmbed,
  card: CardEmbed,
  stat: StatCard,
  action: ActionButton,
  table: DataTable,
  interactive: InteractiveEmbed,
};

function debugEmbedSummary(embed) {
  if (!embed || typeof embed !== 'object') {
    return { jsType: typeof embed, value: embed };
  }

  const payload = embed.payload && typeof embed.payload === 'object' ? embed.payload : null;
  const primitives = Array.isArray(payload?.primitives) ? payload.primitives : [];
  const bindings = Array.isArray(payload?.bindings) ? payload.bindings : [];

  return {
    type: embed.type,
    component: embed.component,
    template: embed.template,
    kind: embed.kind,
    title: embed.title,
    topLevelKeys: Object.keys(embed).sort(),
    payloadKeys: payload ? Object.keys(payload).sort() : null,
    primitiveCount: primitives.length,
    primitiveTypes: primitives.map((primitive) => primitive?.type),
    primitiveIds: primitives.map((primitive) => primitive?.id),
    bindingCount: bindings.length,
    bindingTargets: bindings.map((binding) => binding?.target),
  };
}

function EmbedFallback({ embed }) {
  const title = embed && typeof embed === 'object' && typeof embed.title === 'string' ? embed.title.trim() : '';
  const summary = embed && typeof embed === 'object' && typeof embed.summary === 'string' ? embed.summary.trim() : '';

  return (
    <div className="embed-fallback" role="status">
      {title && <div className="embed-fallback-title">{title}</div>}
      {summary && <div className="embed-fallback-summary">{summary}</div>}
      <div>This interactive view could not be displayed.</div>
    </div>
  );
}

class EmbedDebugBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // TEMP_EMBED_DEBUG: intentionally verbose browser diagnostics for embed rendering.
    // Remove after the embed generation/rendering issue is resolved.
    console.error('TEMP_EMBED_DEBUG frontend_embed_render_error', {
      index: this.props.index,
      summary: debugEmbedSummary(this.props.embed),
      rawEmbed: this.props.embed,
      error,
      componentStack: info?.componentStack,
    });
  }

  render() {
    if (this.state.error) {
      return <EmbedFallback embed={this.props.embed} />;
    }
    return this.props.children;
  }
}

export default function EmbedRenderer({ embeds }) {
  if (!embeds || !Array.isArray(embeds) || embeds.length === 0) return null;
  // TEMP_EMBED_DEBUG: intentionally verbose browser diagnostics for embed rendering.
  // Remove after the embed generation/rendering issue is resolved.
  console.debug('TEMP_EMBED_DEBUG frontend_embed_stack', {
    count: embeds.length,
    summaries: embeds.map(debugEmbedSummary),
    rawEmbeds: embeds,
  });
  return (
    <div className="embed-stack">
      {embeds.map((embed, idx) => {
        if (!embed || typeof embed !== 'object') {
          console.warn('TEMP_EMBED_DEBUG frontend_invalid_embed_value', { index: idx, rawEmbed: embed });
          return <EmbedFallback key={idx} embed={embed} />;
        }
        const Comp = COMPONENT_MAP[embed.type] || COMPONENT_MAP[embed.component];
        if (!Comp) {
          console.warn('TEMP_EMBED_DEBUG frontend_unsupported_embed', {
            index: idx,
            summary: debugEmbedSummary(embed),
            rawEmbed: embed,
          });
          return <EmbedFallback key={idx} embed={embed} />;
        }
        // Always prefer explicit props, otherwise pass the embed object itself.
        const payload = embed.props ? embed.props : embed;
        return (
          <EmbedDebugBoundary key={idx} index={idx} embed={embed}>
            <Comp {...payload} />
          </EmbedDebugBoundary>
        );
      })}
    </div>
  );
}
